/**
 * BaseModal.Media - Media Plugin for BaseModal
 * Handles images, videos and iframes with gallery support
 *
 * @requires BasePlugin.js
 * @version 3.0.0
 */
'use strict';

import BasePlugin         from './BasePlugin.js';
import {generateModalId}  from '../constants.js';
import MediaRenderer      from './media/MediaRenderer.js';
import GalleryManager     from './media/GalleryManager.js';
import NavigationHandler  from './media/NavigationHandler.js';
import ContentUpdater     from './media/ContentUpdater.js';
import ModalHTMLGenerator from './media/ModalHTMLGenerator.js';

/**
 * Default configuration for media modal
 * @type {MediaConfig}
 */
const DEFAULTS = {
    selector               :'[data-basemodal-media]',
    closeAttribute         :'data-basemodal-close',
    gallery                :true,
    autoplay               :false,
    loop                   :false,
    controls               :true,
    preload                :'metadata',
    loading                :'lazy',
    theme                  :'dark',
    animation              :'zoom',
    closeOnVideoEnd        :false,
    fullscreen             :false,
    fullscreenButtonContent:`<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' pointer-events='none'>
        <path d='M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z'
              fill='currentColor'/>
    </svg>`,
    showCloseButton        :true,
    closeButtonContent     :'&times;',
    showOverlay            :true,
    showCounter            :true,
    debugMode              :false,
    locales                :{
        close      :'Close',
        fullscreen :'Toggle Fullscreen',
        prev       :'Previous',
        next       :'Next',
        unsupported:'Unsupported media type'
    }
};

/**
 * BaseModal.Media Class
 * Manages media modals with gallery support
 */
export default class BaseModalMedia extends BasePlugin {
    /**
     * Create a media plugin instance
     * @param {MediaConfig} config - Configuration options
     */
    constructor(config = {}) {
        super(config);
        this.name              = 'BaseModalMedia';
        this.config            = {...DEFAULTS, ...config};
        this.renderer          = new MediaRenderer(this.config);
        this.galleryManager    = new GalleryManager({
            ...this.config,
            renderer:this.renderer
        });
        this.contentUpdater    = new ContentUpdater(this.config);
        this.navigationHandler = null;
        this.htmlGenerator     = new ModalHTMLGenerator({
            ...this.config,
            renderer:this.renderer
        });
    }

    // ========================================
    // INITIALIZATION
    // ========================================

    /**
     * Initialize media plugin
     * Collects all media elements and attaches event listeners
     * @public
     */
    init(baseModal) {
        super.init(baseModal);

        if (!baseModal) {
            console.error('BaseModal.Media: BaseModal instance is required');
            return;
        }

        this.navigationHandler = new NavigationHandler(
            this.config,
            () => this.onNext(),
            () => this.onPrev(),
            (modalEl) => this.onFullscreen(modalEl)
        );
        this.navigationHandler.setDependencies(
            this.renderer,
            this.galleryManager,
            this.contentUpdater
        );

        this.galleryManager.collectMediaElements(this.config.selector);
        this.attachEventListeners();

        if (this.config.debugMode) {
            console.log('BaseModal.Media: Event listeners attached');
        }
    }

    /**
     * Next button callback for NavigationHandler
     * @private
     */
    onNext() {
        void this.nextMedia();
    }

    /**
     * Previous button callback for NavigationHandler
     * @private
     */
    onPrev() {
        void this.prevMedia();
    }

    /**
     * Fullscreen toggle callback for NavigationHandler
     * @param {HTMLElement} modal - Modal element
     * @private
     */
    onFullscreen(modal) {
        this.toggleFullscreen(modal);
    }


    /**
     * Attach click event listeners to media elements
     * @private
     */
    attachEventListeners() {
        this.galleryManager.mediaElements.forEach(mediaData => {
            mediaData.element.addEventListener('click', (e) => {
                e.preventDefault();
                this.show(mediaData).catch(console.error);
            });
        });
    }

    // ========================================
    // MODAL DISPLAY
    // ========================================

    /**
     * Show media in modal
     * @param {MediaData} mediaData - Media data object
     * @returns {Promise} Modal instance promise
     * @public
     */
    async show(mediaData) {
        await this.renderer.ensureImageSize(mediaData);
        this.galleryManager.setCurrentGallery(mediaData);
        const modalId   = generateModalId('media');
        const modalHTML = this.htmlGenerator.generateModalHTML(modalId, mediaData, this.galleryManager);

        document.body.insertAdjacentHTML('beforeend', modalHTML);

        const modal = document.getElementById(modalId);
        if (modal) {
            modal.classList.add(`bm-modal--${mediaData.type}`);

            if (this.config.debugMode) {
                console.log(`BaseModal.Media: Added class BEFORE show: bm-modal--${mediaData.type}`);
            }
        }

        try {
            await this.showModal(modalId, {
                theme              :mediaData.theme || this.config.theme,
                animation          :mediaData.animation || this.config.animation,
                size               :mediaData.size || 'large',
                awaitCloseAnimation:true,
                swipeNavigation    :false,
                dragToDismiss      :false,
                hapticFeedback     :true,
                onClose            :() => {
                    this.cleanup(modalId);
                }
            });

            this.currentModal = modalId;

            if (modal && this.navigationHandler) {
                this.navigationHandler.setup(modal, this.galleryManager);
            }

            this.handleVideoAutoplay(modalId, mediaData);

        } catch (error) {
            console.error('BaseModal.Media: Error showing modal:', error);
        }
    }

    // ========================================
    // MEDIA AUTOPLAY
    // ========================================

    /**
     * Handle video autoplay
     * @param {string} modalId - Modal ID
     * @param {MediaData} mediaData - Media data
     * @private
     */
    handleVideoAutoplay(modalId, mediaData) {
        if (mediaData.type === 'video' && mediaData.autoplay) {
            const video = document.querySelector(`#${modalId} video`);
            if (video) {
                video.play().catch(err => {
                    if (this.config.debugMode) {
                        console.log('BaseModal.Media: Autoplay prevented:', err);
                    }
                });
            }
        }
    }

    // ========================================
    // GALLERY NAVIGATION
    // ========================================

    /**
     * Navigate to next media in gallery
     * @public
     */
    async nextMedia() {
        const nextMediaData = this.galleryManager.next();
        if (!nextMediaData) return;

        await this.updateMediaContent(nextMediaData).catch(console.error);
    }

    /**
     * Navigate to previous media in gallery
     * @public
     */
    async prevMedia() {
        const prevMediaData = this.galleryManager.prev();
        if (!prevMediaData) return;

        await this.updateMediaContent(prevMediaData).catch(console.error);
    }

    /**
     * Update media content in current modal
     * @param {MediaData} mediaData - New media data
     * @private
     */
    async updateMediaContent(mediaData) {
        const modal = document.getElementById(this.currentModal);
        if (!modal) return;

        await this.renderer.ensureImageSize(mediaData);
        this.cleanupMedia(this.currentModal);

        modal.classList.remove('bm-modal--image', 'bm-modal--video', 'bm-modal--iframe');
        modal.classList.add(`bm-modal--${mediaData.type}`);

        if (this.config.debugMode) {
            console.log(`BaseModal.Media: Updated class: bm-modal--${mediaData.type}`);
        }

        const mediaHTML = this.renderer.renderMedia(mediaData);
        const updated   = await this.contentUpdater.updateContent(
            this.currentModal,
            mediaData,
            mediaHTML,
            mediaData.title,
            mediaData.description
        );

        if (updated) {
            this.navigationHandler.updateButtons(modal, this.galleryManager);
            this.handleVideoAutoplay(this.currentModal, mediaData);
        }
    }

    // ========================================
    // UTILITIES
    // ========================================

    /**
     * Toggle fullscreen mode for modal
     * Uses native Fullscreen API
     * @param {HTMLElement} modal - Modal element
     * @public
     */
    toggleFullscreen(modal) {
        if (!modal) return;

        if (!document.fullscreenElement) {
            modal.requestFullscreen().then(() => {
                modal.classList.add('bm-modal--fullscreen');

                const closeContent      = this.config.closeButtonContent || '&times;';
                const closeButton       = this.config.showCloseButton ? this.config.generateCloseButton('Close', closeContent) : '';
                const overlay           = this.config.showOverlay ? `<div class="bm-modal__bg" aria-hidden="true" ${this.config.closeAttribute || 'data-basemodal-close'}></div>` : '';

                if (this.config.debugMode) {
                    console.log('BaseModal.Media: Entered fullscreen mode');
                    console.log('BaseModal.Media: Added class: bm-modal--fullscreen');
                }
            }).catch(err => {
                console.error('BaseModal.Media: Error entering fullscreen:', err);
            });
        } else {
            document.exitFullscreen().then(() => {
                modal.classList.remove('bm-modal--fullscreen');

                if (this.config.debugMode) {
                    console.log('BaseModal.Media: Exited fullscreen mode');
                    console.log('BaseModal.Media: Removed class: bm-modal--fullscreen');
                }
            }).catch(err => {
                console.error('BaseModal.Media: Error exiting fullscreen:', err);
            });
        }
    }

    /**
     * Cleanup media resources
     * Stops videos and clears iframes to prevent memory leaks
     * @param {string} modalId - Modal ID to clean up
     * @private
     */
    cleanupMedia(modalId) {
        const modal = document.getElementById(modalId);
        if (!modal) return;

        const videos = modal.querySelectorAll('video');
        videos.forEach(video => {
            video.pause();
            video.currentTime = 0;
        });

        const iframes = modal.querySelectorAll('iframe');
        iframes.forEach(iframe => {
            iframe.src = '';
        });
    }

    /**
     * Cleanup on modal close
     * @param {string} modalId - Modal ID
     * @private
     */
    cleanup(modalId) {
        this.cleanupMedia(modalId);

        this.contentUpdater.clearTimeout();
        this.contentUpdater.resetTransition();

        const modal = document.getElementById(modalId);
        if (modal && this.navigationHandler) {
            this.navigationHandler.cleanupKeyboard(modal);
        }

        setTimeout(() => {
            if (modal) modal.remove();
        }, 300);
    }

    /**
     * Destroy media modal instance
     * Cleans up all references and galleries
     * @public
     */
    destroy() {
        this.galleryManager.destroy();
        this.currentModal    = null;
        this.isTransitioning = false;

        if (this.navigationHandler) {
            this.navigationHandler = null;
        }

        super.destroy();
    }
}

